import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AppSidebar } from "../components/AppSidebar";
import { MobileSidebar } from "../components/MobileSidebar";
import { AppHeader } from "../components/AppHeader";
import WhatsAppButton from "../components/WhatsAppButton";
import PageTransition from "../components/PageTransition";
import { AnimatePresence } from "framer-motion";
import { X, Menu } from "lucide-react";

// The city-based tool-only gate has been removed: every signed-in user now gets
// the full dashboard shell regardless of toolOnlyAccess / cityUnlocked. Users
// who register through the Verify Listing ad landing page are no longer locked
// to that single tool while their city has no live listings.

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const location = useLocation();

  // Check localStorage on mount to see if video was closed
  useEffect(() => {
    const isVideoClosed = localStorage.getItem("dashboardVideoClosed");
    if (isVideoClosed !== "true") {
      setVideoVisible(true);
    }
    setIsLoaded(true);
  }, []);

  const handleCloseVideo = () => {
    setVideoVisible(false);
    localStorage.setItem("dashboardVideoClosed", "true");
  };

  // Verify Listing needs the width -- a map, a photo grid and a two-column
  // form all compete for it -- so the sidebar auto-collapses to the 72px icon
  // rail on that route and springs back to whatever the user had when they
  // leave. Manual toggling still works while they are on the page.
  const collapsedBeforeWideTool = useRef(null);
  useEffect(() => {
    const isWideTool = location.pathname === "/verify-listing";
    if (isWideTool) {
      if (collapsedBeforeWideTool.current === null) {
        collapsedBeforeWideTool.current = collapsed;
        setCollapsed(true);
      }
    } else if (collapsedBeforeWideTool.current !== null) {
      setCollapsed(collapsedBeforeWideTool.current);
      collapsedBeforeWideTool.current = null;
    }
    // `collapsed` is read as a snapshot on entry only -- adding it to the deps
    // would undo the user's manual toggle on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Hide app sidebar on residential-rental (has its own filter sidebar)
  const isPropertyPage = location.pathname.startsWith("/residential-rental");

  // Sections that own a multi-step flow spread across sub-paths. The page
  // transition below is keyed on the pathname, which remounts the page
  // component on every key change - that wipes the component's state halfway
  // through a flow (e.g. /subscription -> /subscription/checkout would lose
  // the plan the user just picked). These key on the section so the page stays
  // mounted while the user moves between its steps.
  const MULTI_STEP_SECTIONS = ["/subscription"];
  const transitionKey =
    MULTI_STEP_SECTIONS.find((section) => location.pathname.startsWith(section)) ||
    location.pathname;

  return (
    <div className="flex min-h-screen w-full">
      {!isPropertyPage && (
        <AppSidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
        />
      )}
      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <AppHeader onMenuToggle={() => setMobileOpen(true)} />
        <main
          className={`flex-1 overflow-auto hide-scrollbar ${isPropertyPage ? "" : "p-4 md:p-6"}`}
        >
          <AnimatePresence mode="wait">
            <PageTransition key={transitionKey}>
              <Outlet />
            </PageTransition>
          </AnimatePresence>
        </main>
      </div>
      {/* <WhatsAppButton /> */}

      {/* YouTube Video - Bottom Left Corner */}
      {videoVisible && (
        <div className="fixed bottom-20 left-2 sm:bottom-4 sm:left-4 z-40 w-40 sm:w-56 md:w-72 lg:w-80">
          {/* Close Button - Outside the video container */}
          <button
            onClick={handleCloseVideo}
            className="absolute -top-3 -right-3 z-50 w-7 h-7 flex items-center justify-center rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg border-2 border-white transition-colors"
            aria-label="Close video"
          >
            <X size={16} strokeWidth={3} />
          </button>
          {/* Video Container */}
          <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg border border-border bg-background">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/Tp8yKAQWt0M?si=RuU3lqLH8agBKZvt&autoplay=1&mute=1"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;
